import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  getCrmIntegrationLoginUrl,
  getCrmIntegrationLoginUrlFromEvi2ken,
} from '~/api/crms';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

export const SalesforceModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { params, navigate } = useRouting();
  const { clientId } = params;
  const { toast } = useToast();

  const isUsingEvi2ken = useFlag(FlagType.MultipleSfdcMigrationToEvi2ken);

  const action = useSelector(slices.newIntegrations.selectors.selectAction);

  const { executor: fetchIntegrationLoginUrl } = useAsync(
    isUsingEvi2ken
      ? getCrmIntegrationLoginUrlFromEvi2ken
      : getCrmIntegrationLoginUrl,
    {
      integrationUrl: window.location.href,
    },
    {
      errorToastMessage: intl.formatMessage({
        id: 'SalesIntegrations.fetchLoginUrlError',
        defaultMessage:
          'Unable to fetch Salesforce login url, please try again.',
        description: 'Error message when failing to fetch login URL',
      }),
      successHandler: (redirectResponse) => {
        const { redirectUrl } = redirectResponse;
        window.location.href = redirectUrl;
        toast({
          status: 'success',
          message: intl.formatMessage({
            id: 'SalesIntegrations.connectionCreatedSuccess',
            defaultMessage: 'New Salesforce connection has been created.',
            description: 'Success message when new connection is created',
          }),
        });
      },
    },
  );

  if (action !== 'salesforce') {
    return null;
  }

  return (
    <Modal
      title="Salesforce"
      isVisible={true}
      onHide={() => dispatch(slices.newIntegrations.actions.setAction(null))}
    >
      <Layout direction="column" spacing={6}>
        <Layout direction="column" spacing={2}>
          <Text>
            {intl.formatMessage(
              {
                id: 'CrmIntegrationCard.part1InstructionsRichText',
                defaultMessage:
                  '<strong>Part 1:</strong> <instruction>Generate an Evisort API Key to use in your Salesforce configuration.</instruction>',
                description:
                  'Instructions for part 1 of Salesforce integration',
              },
              {
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                instruction: (chunks: React.ReactNode) => (
                  <Text as="span" color="text.secondary">
                    {chunks}
                  </Text>
                ),
              },
            )}
          </Text>
          <Box style={{ justifyContent: 'flex-start' }}>
            <Button
              text={intl.formatMessage({
                id: 'SalesIntegrations.manageApiKey',
                defaultMessage: 'Manage API Key',
                description: 'Button text to manage API key',
              })}
              onClick={() => {
                navigate(`/admin/console/${clientId}/api-management`);
                dispatch(slices.newIntegrations.actions.setAction(null));
              }}
            />
          </Box>
        </Layout>
        <Layout direction="column" spacing={2}>
          <Text>
            {intl.formatMessage(
              {
                id: 'CrmIntegrationCard.part2InstructionsRichText',
                defaultMessage:
                  '<strong>Part 2:</strong> <instruction>Log into your Salesforce account to authenticate and enable the integration.</instruction>',
                description:
                  'Instructions for part 2 of Salesforce integration',
              },
              {
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                instruction: (chunks: React.ReactNode) => (
                  <Text as="span" color="text.quiet">
                    {chunks}
                  </Text>
                ),
              },
            )}
          </Text>
          <Box style={{ justifyContent: 'flex-start' }}>
            <Button
              text={intl.formatMessage({
                id: 'SalesIntegrations.authenticateAccount',
                defaultMessage: 'Authenticate Salesforce ',
                description: 'Button text to authenticate Salesforce account',
              })}
              onClick={fetchIntegrationLoginUrl}
            />
          </Box>
        </Layout>
      </Layout>
    </Modal>
  );
};
